import React from "react";
import { Link } from "react-router-dom";
import {
  TrashIcon,
  EnvelopeSimpleIcon,
  WarningIcon,
  CheckCircleIcon,
  GearSixIcon,
} from "@phosphor-icons/react";

/**
 * Account-deletion landing page — required by the Google Play Console
 * (the "Delete account URL" field). Must be a public HTTPS page that:
 *   1. Names the app + developer.
 *   2. Lists the exact steps a user takes to request deletion.
 *   3. Specifies what's deleted vs. retained, and any retention period.
 *
 * Hosted at /account-deletion so the URL stays stable across builds.
 */
export default function AccountDeletion() {
  return (
    <div className="bg-white min-h-screen text-zinc-800">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link
          to="/"
          className="text-xs font-mono uppercase tracking-widest text-[#1D78AB] hover:underline"
          data-testid="account-deletion-back-home"
        >
          ← Back to Bidorah
        </Link>

        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-[10px] font-mono uppercase tracking-widest">
          <TrashIcon size={12} weight="fill" />
          Account deletion
        </div>

        <h1 className="font-display font-black text-4xl mt-3 mb-2 tracking-tight">
          Delete your Bidorah account
        </h1>
        <p className="text-sm text-zinc-500 mb-10">
          Last updated: April 25, 2026 · Bidorah, Inc.
        </p>

        <div className="space-y-10 text-[15px] leading-relaxed">
          <p>
            This page explains how to permanently delete your{" "}
            <strong>Bidorah</strong> account and the personal data tied to it.
            Bidorah is operated by <strong>Bidorah, Inc.</strong> If you have
            any trouble with the steps below, email{" "}
            <a className="text-[#1D78AB] underline" href="mailto:privacy@bidorah.com">
              privacy@bidorah.com
            </a>{" "}
            and a real human will help.
          </p>

          {/* OPTION 1 — In-app */}
          <section className="border border-zinc-200 rounded-2xl p-6 bg-zinc-50">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-xl bg-[#1D78AB] text-white grid place-items-center">
                <GearSixIcon size={20} weight="fill" />
              </div>
              <h2 className="font-display font-black text-2xl tracking-tight">
                Option 1 · Delete from the Bidorah app or website
              </h2>
            </div>
            <ol className="list-decimal pl-6 space-y-1.5 mt-3">
              <li>
                Open the Bidorah app (or visit{" "}
                <a className="text-[#1D78AB] underline" href="https://bidorah.com">bidorah.com</a>{" "}
                in your browser) and log in.
              </li>
              <li>
                Tap your avatar in the top-right → <strong>Edit profile</strong>{" "}
                (on mobile, tap the <strong>Me</strong> tab).
              </li>
              <li>
                Scroll to the <strong>Danger zone</strong> → tap{" "}
                <strong>Delete my account</strong>.
              </li>
              <li>
                A confirmation popup appears. Tap <strong>OK, delete</strong> to
                confirm, or <strong>Cancel</strong> to keep your account.
              </li>
              <li>
                You&apos;ll be signed out immediately. Your account is queued
                for full deletion within 30 days.
              </li>
            </ol>
          </section>

          {/* OPTION 2 — Email */}
          <section className="border border-zinc-200 rounded-2xl p-6 bg-white">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-xl bg-zinc-900 text-white grid place-items-center">
                <EnvelopeSimpleIcon size={20} weight="fill" />
              </div>
              <h2 className="font-display font-black text-2xl tracking-tight">
                Option 2 · Email Bidorah privacy
              </h2>
            </div>
            <ol className="list-decimal pl-6 space-y-1.5 mt-3">
              <li>
                Send an email to{" "}
                <a className="text-[#1D78AB] underline" href="mailto:privacy@bidorah.com?subject=Delete%20my%20Bidorah%20account">
                  privacy@bidorah.com
                </a>{" "}
                from the address registered on your Bidorah account.
              </li>
              <li>
                Subject line: <em>&quot;Delete my Bidorah account&quot;</em>.
              </li>
              <li>
                In the body include your account email and (optional) the
                reason you&apos;re leaving — feedback helps us improve.
              </li>
              <li>
                Bidorah confirms within <strong>3 business days</strong> and
                completes the deletion within <strong>30 days</strong> of
                confirmation.
              </li>
            </ol>
          </section>

          {/* What gets deleted */}
          <section>
            <div className="flex items-center gap-3 mb-3">
              <CheckCircleIcon size={22} weight="fill" className="text-emerald-600" />
              <h2 className="font-display font-black text-2xl tracking-tight">
                What is deleted
              </h2>
            </div>
            <p>
              When your account is deleted, Bidorah permanently removes:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Your name, email, and password hash.</li>
              <li>Your profile picture, bio, and store / display name.</li>
              <li>Your saved searches, follows, and bookmarked listings.</li>
              <li>Your chat messages, tap-to-react history, and stream-watch logs.</li>
              <li>Your uploaded government photo ID (deleted within 180 days of account closure, or sooner if not subject to an active dispute).</li>
              <li>Your Founder Wallet credit balance (if any). Cashback already paid out is not refundable.</li>
              <li>Your push-notification tokens and email subscription state.</li>
            </ul>
          </section>

          {/* What is retained */}
          <section>
            <div className="flex items-center gap-3 mb-3">
              <WarningIcon size={22} weight="fill" className="text-amber-500" />
              <h2 className="font-display font-black text-2xl tracking-tight">
                What is retained, and for how long
              </h2>
            </div>
            <p>
              U.S. tax, anti-money-laundering, and consumer-protection law
              require Bidorah to keep a minimal record of past activity even
              after your account is closed. Bidorah retains the following for{" "}
              <strong>seven (7) years</strong>, then anonymises or deletes it:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Order history (item, price, date) — required for tax reporting.</li>
              <li>Payout records (to sellers) and refund records (to buyers) — required by Stripe, PayPal, and IRS Form 1099-K reporting.</li>
              <li>Wholesale-buyer credentials (Reseller&apos;s Permit, last four digits of TIN, registered business entity) — required for sales-tax exemption audits.</li>
              <li>Identity-verification status (approved / rejected, plus timestamp) — required for fraud-prevention and re-entry blocks.</li>
              <li>Trust-and-safety records (bans, abuse reports, dispute outcomes).</li>
              <li>Aggregated, anonymised analytics that no longer identify you.</li>
            </ul>
            <p className="mt-3">
              Bidorah does <strong>not</strong> sell or share retained data with
              third parties for advertising or marketing purposes.
            </p>
          </section>

          {/* Partial deletion */}
          <section>
            <h2 className="font-display font-black text-2xl tracking-tight mb-2">
              Want to delete only some of your data?
            </h2>
            <p>
              You can also ask Bidorah to delete specific data without closing
              your account — for example, removing a single listing, a chat
              message, or your photo ID. Email{" "}
              <a className="text-[#1D78AB] underline" href="mailto:privacy@bidorah.com">
                privacy@bidorah.com
              </a>{" "}
              with the details and we&apos;ll action it within 30 days.
            </p>
          </section>

          {/* Reactivation */}
          <section>
            <h2 className="font-display font-black text-2xl tracking-tight mb-2">
              Changed your mind?
            </h2>
            <p>
              Within 30 days of submitting the deletion request, email{" "}
              <a className="text-[#1D78AB] underline" href="mailto:privacy@bidorah.com">
                privacy@bidorah.com
              </a>{" "}
              from the same address and we&apos;ll cancel the queued deletion
              and restore your account. After 30 days the deletion is
              irreversible — you would have to register a fresh account, and
              your prior bidding history, ratings, and Founder badge cannot be
              recovered.
            </p>
          </section>

          <section>
            <h2 className="font-display font-black text-2xl tracking-tight mb-2">
              Contact
            </h2>
            <p>
              Bidorah, Inc.<br />
              Privacy &amp; deletion requests:{" "}
              <a className="text-[#1D78AB] underline" href="mailto:privacy@bidorah.com">
                privacy@bidorah.com
              </a>
              <br />
              General support:{" "}
              <a className="text-[#1D78AB] underline" href="mailto:support@bidorah.com">
                support@bidorah.com
              </a>
            </p>
          </section>

          <p className="text-sm text-zinc-500 italic pt-4 border-t border-zinc-200">
            Bidorah is a U.S. registered marketplace. Deletion of wholesale
            accounts is subject to additional verification because of
            sales-tax exemption recordkeeping.
          </p>
        </div>
      </div>
    </div>
  );
}
